import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle, Upload, FileCode } from 'lucide-react';
import { Workflow } from '@/types/workflow';
import { Badge } from '@/components/ui/badge';
import { adminApi } from '@/lib/api';
import { workflowImportService } from '@/services/workflowImportService';

interface LocalWorkflowMetadata {
  filename: string;
  slug: string | null;
  title: string | null;
  hasExport: boolean;
  imported: boolean;
  dbWorkflow: {
    id: string;
    version: number;
    status: string;
    updatedAt: string;
  } | null;
}

interface ImportLocalWorkflowDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  onSuccess?: (imported: Workflow[]) => void;
}

export const ImportLocalWorkflowDialog: React.FC<ImportLocalWorkflowDialogProps> = ({
  open,
  onOpenChange,
  userId,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workflows, setWorkflows] = useState<LocalWorkflowMetadata[]>([]);
  const [importing, setImporting] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);

  // Fetch local workflows when dialog opens
  useEffect(() => {
    if (open) {
      fetchLocalWorkflows();
    } else {
      // Reset state when dialog closes
      setImportSuccess(null);
      setError(null);
    }
  }, [open]);

  const fetchLocalWorkflows = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await adminApi.getLocalWorkflowsMetadata();

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch local workflows');
      }

      setWorkflows(response.data || []);
    } catch (err: any) {
      console.error('Failed to fetch local workflows:', err);
      setError(err.message || 'Failed to load local workflows');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (workflow: LocalWorkflowMetadata) => {
    if (!workflow.slug) {
      setError('Workflow is missing slug');
      return;
    }

    try {
      setImporting(workflow.slug);
      setError(null);

      // Fetch the workflow JSON from the backend
      const jsonResponse = await adminApi.getLocalWorkflowJson(workflow.filename);

      if (!jsonResponse.success || !jsonResponse.data) {
        throw new Error(jsonResponse.error || 'Failed to load workflow JSON');
      }

      const workflowData = jsonResponse.data;

      // Check if workflow already exists
      const { exists, workflow: existingWorkflow } = await workflowImportService.checkWorkflowExists(
        workflow.slug
      );

      let imported: Workflow;
      if (exists && existingWorkflow) {
        // Update existing workflow
        imported = await workflowImportService.updateFromLocal(existingWorkflow.id, workflowData, userId);
      } else {
        // Create new workflow
        imported = await workflowImportService.importWorkflow(workflowData, userId);
      }

      setImportSuccess(workflow.slug);

      // Call success callback
      if (onSuccess) {
        onSuccess([imported]);
      }

      // Refresh the list after a delay
      setTimeout(() => {
        setImportSuccess(null);
        fetchLocalWorkflows();
      }, 2000);
    } catch (err: any) {
      console.error('Import failed:', err);
      setError(err.message || 'Failed to import workflow');
    } finally {
      setImporting(null);
    }
  };

  const validWorkflows = workflows.filter((w) => w.slug && w.hasExport);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import guided flows</DialogTitle>
          <DialogDescription>
            Copy a bundled guided flow (packages/artifacts/guided-flows) into the database so it can be edited here. Bundled guided flows already run without importing; assign them to an agent in Edit Agent &gt; Flows.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Workflow List */}
          {!loading && validWorkflows.length > 0 && (
            <>
              {/* Workflow Items */}
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {validWorkflows.map((workflow) => {
                  const isImported = workflow.imported;
                  const isImporting = importing === workflow.slug;
                  const isSuccess = importSuccess === workflow.slug;

                  return (
                    <div
                      key={workflow.slug}
                      className="border rounded-lg p-4 hover:border-gray-300 dark:hover:border-gray-700 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="font-medium truncate">{workflow.title || workflow.slug}</div>
                              <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                                <FileCode className="h-3 w-3" />
                                <span className="truncate">{workflow.filename}</span>
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">Slug: {workflow.slug}</div>
                            </div>

                            {/* Status Badge & Action */}
                            <div className="flex flex-col gap-2 items-end">
                              {isSuccess ? (
                                <Badge variant="outline" className="text-green-600 border-green-600">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  {isImported ? 'Updated!' : 'Imported!'}
                                </Badge>
                              ) : isImported ? (
                                <>
                                  <Badge variant="outline" className="text-green-600 border-green-600">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Imported
                                  </Badge>
                                  {workflow.dbWorkflow && (
                                    <div className="text-xs text-muted-foreground">
                                      v{workflow.dbWorkflow.version} · {workflow.dbWorkflow.status}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <Badge variant="outline" className="text-blue-600 border-blue-600">
                                  New
                                </Badge>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleImport(workflow)}
                                disabled={isImporting || isSuccess}
                              >
                                {isImporting ? (
                                  <>
                                    <div className="animate-spin rounded-full h-3 w-3 mr-1 border-b-2 border-current"></div>
                                    Importing...
                                  </>
                                ) : (
                                  <>
                                    <Upload className="h-3 w-3 mr-1" />
                                    {isImported ? 'Update' : 'Import'}
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Empty State */}
          {!loading && validWorkflows.length === 0 && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No local workflows found. Make sure the artifacts package has been built with workflow JSON files exported to dist/workflows/.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
